/**
 * Shared floorplan list filter logic for /floorplans and /community-map.
 * Page scripts own apply behavior; this module owns toolbar state, matching rules, session storage, and filter bubbles.
 */
(function initRvFloorplanFilterUtils() {
    const SESSION_STORAGE_KEY = 'rv-floorplan-list-filters';

    function getSelectedValuesForFilter(filterToolbar, dimension) {
        const values = [];
        if (filterToolbar == null) {
            return values;
        }
        filterToolbar
            .querySelectorAll('input[type="checkbox"][data-filter-dimension="' + dimension + '"]:checked')
            .forEach(function (checkbox) {
                values.push(checkbox.value);
            });
        return values;
    }

    function parsePriceFilter() {
        const priceMinField = document.getElementById('floorplan-filter-price-min');
        const priceMaxField = document.getElementById('floorplan-filter-price-max');
        const priceMinString = priceMinField ? priceMinField.value.trim() : '';
        const priceMaxString = priceMaxField ? priceMaxField.value.trim() : '';
        const priceFilterHasValue = priceMinString !== '' || priceMaxString !== '';

        if (priceFilterHasValue) {
            let priceMin = parseFloat(priceMinString);
            let priceMax = parseFloat(priceMaxString);
            if (!Number.isFinite(priceMin)) {
                priceMin = -Infinity;
            }
            if (!Number.isFinite(priceMax)) {
                priceMax = Infinity;
            }
            if (priceMin > priceMax) {
                const swap = priceMin;
                priceMin = priceMax;
                priceMax = swap;
            }
            return { priceFilterLow: priceMin, priceFilterHigh: priceMax };
        }
        return null;
    }

    function getFilterStateFromToolbar(filterToolbar) {
        return {
            selectedUnitTypes: getSelectedValuesForFilter(filterToolbar, 'unit-type'),
            selectedBedCounts: getSelectedValuesForFilter(filterToolbar, 'bed-count'),
            selectedPetTypes: getSelectedValuesForFilter(filterToolbar, 'pets'),
            selectedAmenities: getSelectedValuesForFilter(filterToolbar, 'amenities').map(function (name) {
                return name.trim().toLowerCase();
            }),
            selectedFloorplans: getSelectedValuesForFilter(filterToolbar, 'floorplan'),
            selectedPriceRange: parsePriceFilter(),
        };
    }

    function filterStateHasActiveSelections(filterState) {
        if (filterState == null) {
            return false;
        }
        return (
            filterState.selectedUnitTypes.length > 0 ||
            filterState.selectedBedCounts.length > 0 ||
            filterState.selectedPetTypes.length > 0 ||
            filterState.selectedAmenities.length > 0 ||
            filterState.selectedFloorplans.length > 0 ||
            filterState.selectedPriceRange != null
        );
    }

    function attributeInSelection(attributeValue, selectedValues) {
        if (selectedValues.length === 0) {
            return true;
        }
        return selectedValues.indexOf(attributeValue || '') !== -1;
    }

    function priceRangeOverlapsFilter(priceMin, priceMax, selectedPriceRange) {
        if (selectedPriceRange == null) {
            return true;
        }
        if (priceMin == null && priceMax == null) {
            return true;
        }

        let floorplanLowPrice = priceMin;
        let floorplanHighPrice = priceMax;
        if (floorplanLowPrice == null && floorplanHighPrice == null) {
            return false;
        } else if (floorplanLowPrice != null && floorplanHighPrice == null) {
            floorplanHighPrice = floorplanLowPrice;
        } else if (floorplanLowPrice == null && floorplanHighPrice != null) {
            floorplanLowPrice = floorplanHighPrice;
        }

        return (
            selectedPriceRange.priceFilterLow <= floorplanHighPrice &&
            selectedPriceRange.priceFilterHigh >= floorplanLowPrice
        );
    }

    function petTypeMatches(petTypeCodename, selectedPetTypes) {
        if (selectedPetTypes.length === 0) {
            return true;
        }
        const value = petTypeCodename || '';
        // CATDOG floorplans/units match when either cats or dogs is selected
        if (value === 'CATDOG') {
            return selectedPetTypes.indexOf('CATS') !== -1 || selectedPetTypes.indexOf('DOGS') !== -1;
        }
        return selectedPetTypes.indexOf(value) !== -1;
    }

    function amenitiesMatch(amenityNames, selectedAmenities) {
        if (selectedAmenities.length === 0) {
            return true;
        }
        const normalizedAmenities = amenityNames ?? [];
        return selectedAmenities.every(function (amenity) {
            return normalizedAmenities.indexOf(amenity) !== -1;
        });
    }

    function getFloorplanAmenityNames(floorplanElement) {
        const raw = floorplanElement.getAttribute('data-amenities') || '';
        return raw
            .split('|')
            .map(function (segment) {
                return segment.trim().toLowerCase();
            })
            .filter(Boolean);
    }

    function floorplanElementMatchesFilters(floorplanElement, filterState) {
        if (!attributeInSelection(floorplanElement.getAttribute('data-property-type'), filterState.selectedUnitTypes)) {
            return false;
        }
        if (!attributeInSelection(floorplanElement.getAttribute('data-bedrooms'), filterState.selectedBedCounts)) {
            return false;
        }
        if (
            filterState.selectedPriceRange &&
            !floorplanRentRangeOverlapsPriceFilter(floorplanElement, filterState.selectedPriceRange)
        ) {
            return false;
        }
        if (!floorplanPetTypeMatch(floorplanElement, filterState.selectedPetTypes)) {
            return false;
        }
        if (!floorplanAmenitiesMatch(floorplanElement, filterState.selectedAmenities)) {
            return false;
        }
        if (!attributeInSelection(floorplanElement.getAttribute('data-floorplan-id'), filterState.selectedFloorplans)) {
            return false;
        }
        return true;
    }

    function floorplanRentRangeOverlapsPriceFilter(floorplan, selectedPriceRange) {
        let floorplanLowPriceString = floorplan.getAttribute('data-price-min');
        let floorplanHighPriceString = floorplan.getAttribute('data-price-max');
        floorplanLowPriceString = floorplanLowPriceString ? floorplanLowPriceString.trim() : '';
        floorplanHighPriceString = floorplanHighPriceString ? floorplanHighPriceString.trim() : '';

        if (floorplanLowPriceString === '' && floorplanHighPriceString === '') {
            return true;
        }

        let floorplanLowPrice = parseFloat(floorplanLowPriceString);
        let floorplanHighPrice = parseFloat(floorplanHighPriceString);
        if (!Number.isFinite(floorplanLowPrice) && !Number.isFinite(floorplanHighPrice)) {
            return false;
        } else if (Number.isFinite(floorplanLowPrice) && !Number.isFinite(floorplanHighPrice)) {
            floorplanHighPrice = floorplanLowPrice;
        } else if (!Number.isFinite(floorplanLowPrice) && Number.isFinite(floorplanHighPrice)) {
            floorplanLowPrice = floorplanHighPrice;
        }

        return priceRangeOverlapsFilter(floorplanLowPrice, floorplanHighPrice, selectedPriceRange);
    }

    function floorplanPetTypeMatch(floorplan, selectedPetTypes) {
        return petTypeMatches(floorplan.getAttribute('data-pet-type'), selectedPetTypes);
    }

    function floorplanAmenitiesMatch(floorplan, selectedAmenities) {
        return amenitiesMatch(getFloorplanAmenityNames(floorplan), selectedAmenities);
    }

    function getUnitPriceRange(unit) {
        if (unit.hasTempPricing) {
            const priceMin = unit.temporaryPriceMin ?? unit.temporaryPriceMax ?? null;
            const priceMax = unit.temporaryPriceMax ?? unit.temporaryPriceMin ?? null;
            return { priceMin: priceMin, priceMax: priceMax };
        }
        if (unit.rentPriceOverride && unit.rentPriceStartsWith) {
            return { priceMin: unit.rentPriceLow ?? null, priceMax: unit.rentPriceHigh ?? null };
        }
        if (unit.rentPriceOverride) {
            return { priceMin: null, priceMax: null };
        }
        return { priceMin: unit.rentPriceLow ?? null, priceMax: unit.rentPriceHigh ?? null };
    }

    function unitRentRangeOverlapsPriceFilter(unit, selectedPriceRange) {
        const priceRange = getUnitPriceRange(unit);
        return priceRangeOverlapsFilter(priceRange.priceMin, priceRange.priceMax, selectedPriceRange);
    }

    function unitMatchesFilters(unit, filterState) {
        if (!attributeInSelection(String(unit.propertyTypeId), filterState.selectedUnitTypes)) {
            return false;
        }
        if (!attributeInSelection(String(unit.numBedrooms), filterState.selectedBedCounts)) {
            return false;
        }
        if (filterState.selectedPriceRange && !unitRentRangeOverlapsPriceFilter(unit, filterState.selectedPriceRange)) {
            return false;
        }
        if (!petTypeMatches(unit.petTypeCodename, filterState.selectedPetTypes)) {
            return false;
        }
        if (!amenitiesMatch(unit.amenityNames, filterState.selectedAmenities)) {
            return false;
        }
        if (!attributeInSelection(String(unit.rentvisionFloorplanId), filterState.selectedFloorplans)) {
            return false;
        }
        return true;
    }

    function readStoredFilters() {
        try {
            const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
            if (raw == null || raw === '') {
                return null;
            }
            const parsed = JSON.parse(raw);
            if (parsed == null || typeof parsed !== 'object') {
                return null;
            }
            return parsed;
        } catch (ignored) {
            return null;
        }
    }

    function storedFiltersWithoutDimensions(stored, excludedDimensions) {
        if (stored == null || !Array.isArray(excludedDimensions) || excludedDimensions.length === 0) {
            return stored;
        }
        const storedCheckboxes = stored.checkboxes;
        if (storedCheckboxes == null || typeof storedCheckboxes !== 'object') {
            return stored;
        }
        const restoredCheckboxes = {};
        Object.keys(storedCheckboxes).forEach(function (dimension) {
            if (excludedDimensions.indexOf(dimension) === -1) {
                restoredCheckboxes[dimension] = storedCheckboxes[dimension];
            }
        });
        const restored = { checkboxes: restoredCheckboxes };
        if (stored.price != null) {
            restored.price = stored.price;
        }
        return restored;
    }

    function restoreFiltersToToolbar(filterToolbar, stored) {
        if (filterToolbar == null || stored == null) {
            return;
        }

        if (stored.checkboxes != null && typeof stored.checkboxes === 'object') {
            Object.keys(stored.checkboxes).forEach(function (dimension) {
                const storedValues = stored.checkboxes[dimension];
                if (!Array.isArray(storedValues)) {
                    return;
                }

                const dimensionCheckboxes = filterToolbar.querySelectorAll(
                    'input[type="checkbox"][data-filter-dimension="' + dimension + '"]',
                );
                if (dimensionCheckboxes.length === 0) {
                    return;
                }

                dimensionCheckboxes.forEach(function (checkbox) {
                    checkbox.checked = false;
                });

                storedValues.forEach(function (storedValue) {
                    dimensionCheckboxes.forEach(function (checkbox) {
                        if (checkbox.value === storedValue) {
                            checkbox.checked = true;
                        }
                    });
                });
            });
        }

        if (stored.price != null && typeof stored.price === 'object') {
            const priceMinField = document.getElementById('floorplan-filter-price-min');
            const priceMaxField = document.getElementById('floorplan-filter-price-max');
            if (priceMinField && typeof stored.price.min === 'string') {
                priceMinField.value = stored.price.min;
            }
            if (priceMaxField && typeof stored.price.max === 'string') {
                priceMaxField.value = stored.price.max;
            }
        }
    }

    function getFloorplansPluralLabel() {
        if (typeof FLOORPLANS_PLURAL_LABEL !== 'undefined' && FLOORPLANS_PLURAL_LABEL !== '') {
            return FLOORPLANS_PLURAL_LABEL;
        }
        return 'Floorplans';
    }

    function getSelectedLabelsForFilter(filterToolbar, dimension) {
        const labels = [];
        if (filterToolbar == null) {
            return labels;
        }
        filterToolbar
            .querySelectorAll('input[type="checkbox"][data-filter-dimension="' + dimension + '"]:checked')
            .forEach(function (checkbox) {
                const filterItem = checkbox.closest('.filter-item');
                const labelEl = filterItem ? filterItem.querySelector('label') : null;
                if (labelEl && labelEl.getAttribute('data-filter-bubble-label')) {
                    labels.push(labelEl.getAttribute('data-filter-bubble-label').trim());
                } else if (labelEl && labelEl.textContent.trim()) {
                    labels.push(labelEl.textContent.trim());
                } else {
                    labels.push(checkbox.value);
                }
            });
        return labels;
    }

    function formatPriceFilterBubbleAmount(rawString) {
        const n = parseFloat(rawString);
        if (Number.isFinite(n)) {
            return (
                '$' +
                n.toLocaleString('en-US', {
                    maximumFractionDigits: 0,
                })
            );
        }
        return '$' + rawString;
    }

    function buildBedCountFilterBubbleText(filterToolbar) {
        const labels = getSelectedLabelsForFilter(filterToolbar, 'bed-count');
        if (labels.length === 0) {
            return null;
        }
        return labels.join(', ').replace(/\sBed(?=.*\sBed)/g, '');
    }

    function buildPriceFilterBubbleText() {
        const priceMinField = document.getElementById('floorplan-filter-price-min');
        const priceMaxField = document.getElementById('floorplan-filter-price-max');
        const minStr = priceMinField ? priceMinField.value.trim() : '';
        const maxStr = priceMaxField ? priceMaxField.value.trim() : '';
        if (minStr === '' && maxStr === '') {
            return null;
        }
        if (minStr !== '' && maxStr !== '') {
            return formatPriceFilterBubbleAmount(minStr) + ' – ' + formatPriceFilterBubbleAmount(maxStr);
        }
        if (minStr !== '') {
            return 'From ' + formatPriceFilterBubbleAmount(minStr);
        }
        return 'Up to ' + formatPriceFilterBubbleAmount(maxStr);
    }

    function buildActiveFilterBubbles(filterToolbar, floorplansPluralLabel) {
        const filterBubbles = [];

        function pushCommaSeparatedDimensionFilterBubble(dimension, filterLabel) {
            const labels = getSelectedLabelsForFilter(filterToolbar, dimension);
            if (labels.length === 0) {
                return;
            }
            filterBubbles.push({
                dimension: dimension,
                filterLabel: filterLabel,
                text: labels.join(', '),
            });
        }

        function pushSingleOrCountSummaryFilterBubble(dimension, multiSelectLabel) {
            const labels = getSelectedLabelsForFilter(filterToolbar, dimension);
            if (labels.length === 1) {
                filterBubbles.push({
                    dimension: dimension,
                    filterLabel: multiSelectLabel,
                    text: labels[0],
                });
            } else if (labels.length > 1) {
                filterBubbles.push({
                    dimension: dimension,
                    filterLabel: multiSelectLabel,
                    text: labels.length + ' ' + multiSelectLabel,
                });
            }
        }

        pushCommaSeparatedDimensionFilterBubble('unit-type', 'Unit Type');

        const bedCountText = buildBedCountFilterBubbleText(filterToolbar);
        if (bedCountText != null) {
            filterBubbles.push({
                dimension: 'bed-count',
                filterLabel: 'Bed Count',
                text: bedCountText,
            });
        }

        const priceText = buildPriceFilterBubbleText();
        if (priceText !== null) {
            filterBubbles.push({
                dimension: 'price',
                filterLabel: 'Price',
                text: priceText,
            });
        }

        pushCommaSeparatedDimensionFilterBubble('pets', 'Pets');
        pushSingleOrCountSummaryFilterBubble('amenities', 'Amenities');
        pushSingleOrCountSummaryFilterBubble('floorplan', floorplansPluralLabel);

        return filterBubbles;
    }

    function syncActiveFilterBubbles(filterToolbar, options) {
        const activeFiltersStrip = document.getElementById('floorplan-list-active-filters');
        const bubblesList = document.getElementById('floorplan-list-filter-bubbles');
        const filterPopupResetAllV2Button =
            options?.filterPopupResetAllButton ?? document.getElementById('floorplan-list-popup-reset-filters');
        const floorplansPluralLabel = options?.floorplansPluralLabel ?? getFloorplansPluralLabel();

        if (activeFiltersStrip == null || bubblesList == null) {
            return;
        }

        const filterBubbles = buildActiveFilterBubbles(filterToolbar, floorplansPluralLabel);
        bubblesList.innerHTML = '';
        filterBubbles.forEach(function (filterBubble) {
            const filterButton = document.createElement('button');
            filterButton.className = 'blueButton fixedHoverEffect floorplan-list-filter-bubble';
            filterButton.type = 'button';
            filterButton.setAttribute('title', 'Clear Filter');
            filterButton.setAttribute('data-floorplan-list-filter-dimension', filterBubble.dimension);

            const textSpan = document.createElement('span');
            textSpan.className = 'floorplan-list-filter-bubble-text';
            textSpan.textContent = filterBubble.text;

            const removeX = document.createElement('span');
            removeX.className = 'floorplan-list-filter-bubble-x';
            removeX.textContent = '\u00D7';

            const ariaText = document.createElement('span');
            ariaText.className = 'hiddenIR';
            ariaText.textContent = 'Clear ' + filterBubble.filterLabel + ' Filter';

            filterButton.appendChild(textSpan);
            filterButton.appendChild(removeX);
            filterButton.appendChild(ariaText);

            bubblesList.appendChild(filterButton);
        });

        if (filterBubbles.length === 0) {
            activeFiltersStrip.classList.add('hidden');
            if (filterPopupResetAllV2Button != null) {
                filterPopupResetAllV2Button.classList.add('hidden');
            }
        } else {
            activeFiltersStrip.classList.remove('hidden');
            if (filterPopupResetAllV2Button != null) {
                filterPopupResetAllV2Button.classList.remove('hidden');
            }
        }
    }

    function clearCheckboxFilter(filterToolbar, dimension) {
        if (filterToolbar == null) {
            return;
        }
        filterToolbar
            .querySelectorAll('input[type="checkbox"][data-filter-dimension="' + dimension + '"]')
            .forEach(function (checkbox) {
                checkbox.checked = false;
            });
        filterToolbar.dispatchEvent(new Event('change', { bubbles: true }));
    }

    function clearPriceFilter(filterToolbar) {
        const priceMinField = document.getElementById('floorplan-filter-price-min');
        const priceMaxField = document.getElementById('floorplan-filter-price-max');
        if (priceMinField) {
            priceMinField.value = '';
        }
        if (priceMaxField) {
            priceMaxField.value = '';
        }
        if (filterToolbar != null) {
            filterToolbar.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }

    function clearAllFilters(filterToolbar, blockChangeEvent) {
        if (filterToolbar == null) {
            return;
        }
        filterToolbar.querySelectorAll('input[type="checkbox"][data-filter-dimension]').forEach(function (checkbox) {
            checkbox.checked = false;
        });
        const priceMinField = document.getElementById('floorplan-filter-price-min');
        const priceMaxField = document.getElementById('floorplan-filter-price-max');
        if (priceMinField) {
            priceMinField.value = '';
        }
        if (priceMaxField) {
            priceMaxField.value = '';
        }
        if (!blockChangeEvent) {
            filterToolbar.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }

    function initActiveFilterStrip(filterToolbar, options) {
        const activeFiltersStrip = document.getElementById('floorplan-list-active-filters');
        const resetAllButton = document.getElementById('floorplan-list-reset-all-filters');

        if (activeFiltersStrip != null) {
            activeFiltersStrip.addEventListener('click', function (clickEvent) {
                const removeButton = clickEvent.target.closest('[data-floorplan-list-filter-dimension]');
                if (removeButton == null || !activeFiltersStrip.contains(removeButton)) {
                    return;
                }
                const dimension = removeButton.getAttribute('data-floorplan-list-filter-dimension');
                if (typeof trackGA4Event === 'function' && dimension != null) {
                    trackGA4Event('clear_filter_' + dimension.replace('-', '_'));
                }
                if (dimension === 'price') {
                    clearPriceFilter(filterToolbar);
                } else if (dimension != null) {
                    clearCheckboxFilter(filterToolbar, dimension);
                }
            });
        }

        if (resetAllButton != null) {
            resetAllButton.addEventListener('click', function () {
                if (typeof trackGA4Event === 'function') {
                    trackGA4Event('reset_filters');
                }
                clearAllFilters(filterToolbar);
            });
        }
    }

    function trackApplyFilterEvent(filterDimension) {
        if (filterDimension == null || filterDimension === '') {
            return;
        }
        const sessionKey = 'rv-tracked-fl-filter-' + filterDimension;
        if (sessionStorage.getItem(sessionKey) === 'true') {
            return;
        }
        if (typeof trackGA4Event === 'function') {
            trackGA4Event('apply_filter_' + filterDimension.replace('-', '_'));
        }
        sessionStorage.setItem(sessionKey, 'true');
    }

    /**
     * Wires toolbar change listeners, optional session restore, and filter bubble clicks.
     * Page scripts pass onFiltersChanged to define what "apply filters" means on that page.
     */
    function initFilterToolbar(options) {
        const filterToolbar = options?.filterToolbar ?? document.getElementById('floorplan-list-filters-container');
        if (filterToolbar == null) {
            return null;
        }

        const onFiltersChanged = options?.onFiltersChanged;
        const onToolbarChange = options?.onToolbarChange;
        const restoreFromSession = options?.restoreFromSession !== false;
        const sessionRestoreExcludedDimensions = options?.sessionRestoreExcludedDimensions ?? [];
        const priceDebounceMs = options?.priceDebounceMs ?? 150;

        initActiveFilterStrip(filterToolbar, {
            filterPopupResetAllButton: options?.filterPopupResetAllButton,
            floorplansPluralLabel: options?.floorplansPluralLabel,
        });

        let priceFilterDebounceTimerId;
        function scheduleDebouncedApply() {
            clearTimeout(priceFilterDebounceTimerId);
            priceFilterDebounceTimerId = setTimeout(function () {
                if (typeof onFiltersChanged === 'function') {
                    onFiltersChanged();
                }
            }, priceDebounceMs);
        }

        filterToolbar.addEventListener('change', function (changeEvent) {
            trackApplyFilterEvent(changeEvent.target.dataset.filterDimension);
            if (typeof onToolbarChange === 'function') {
                onToolbarChange(changeEvent);
            }
            if (typeof onFiltersChanged === 'function') {
                onFiltersChanged();
            }
        });

        const priceFilterInputs = document.getElementsByClassName('floorplan-filter-price');
        Array.from(priceFilterInputs).forEach(function (priceFilterInput) {
            priceFilterInput.addEventListener('input', scheduleDebouncedApply);
        });

        if (restoreFromSession) {
            restoreFiltersToToolbar(
                filterToolbar,
                storedFiltersWithoutDimensions(readStoredFilters(), sessionRestoreExcludedDimensions),
            );
        }

        filterToolbar.dispatchEvent(new Event('change', { bubbles: true }));

        return {
            filterToolbar: filterToolbar,
            getFilterState: function () {
                return getFilterStateFromToolbar(filterToolbar);
            },
            clearAllFilters: function (blockChangeEvent) {
                clearAllFilters(filterToolbar, blockChangeEvent);
            },
        };
    }

    function saveFiltersToSessionStorage(filterToolbar) {
        if (filterToolbar == null) {
            return;
        }
        const checkboxesByDimension = {};
        filterToolbar.querySelectorAll('input[type="checkbox"][data-filter-dimension]').forEach(function (checkbox) {
            if (!checkbox.checked) {
                return;
            }
            const dimension = checkbox.getAttribute('data-filter-dimension');
            if (dimension == null || dimension === '') {
                return;
            }
            if (!checkboxesByDimension[dimension]) {
                checkboxesByDimension[dimension] = [];
            }
            checkboxesByDimension[dimension].push(checkbox.value);
        });

        const priceMinField = document.getElementById('floorplan-filter-price-min');
        const priceMaxField = document.getElementById('floorplan-filter-price-max');
        const minStr = priceMinField ? priceMinField.value.trim() : '';
        const maxStr = priceMaxField ? priceMaxField.value.trim() : '';

        const payload = { checkboxes: checkboxesByDimension };
        if (minStr !== '' || maxStr !== '') {
            payload.price = { min: minStr, max: maxStr };
        }

        try {
            const hasCheckboxSelections = Object.keys(checkboxesByDimension).some(function (dimension) {
                return checkboxesByDimension[dimension].length > 0;
            });
            if (!hasCheckboxSelections && payload.price == null) {
                sessionStorage.removeItem(SESSION_STORAGE_KEY);
                return;
            }
            sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(payload));
        } catch (ignored) {}
    }

    window.RvFloorplanFilterUtils = {
        SESSION_STORAGE_KEY: SESSION_STORAGE_KEY,
        getSelectedValuesForFilter: getSelectedValuesForFilter,
        getSelectedLabelsForFilter: getSelectedLabelsForFilter,
        parsePriceFilter: parsePriceFilter,
        getFilterStateFromToolbar: getFilterStateFromToolbar,
        filterStateHasActiveSelections: filterStateHasActiveSelections,
        floorplanElementMatchesFilters: floorplanElementMatchesFilters,
        unitMatchesFilters: unitMatchesFilters,
        readStoredFilters: readStoredFilters,
        restoreFiltersToToolbar: restoreFiltersToToolbar,
        saveFiltersToSessionStorage: saveFiltersToSessionStorage,
        syncActiveFilterBubbles: syncActiveFilterBubbles,
        clearCheckboxFilter: clearCheckboxFilter,
        clearPriceFilter: clearPriceFilter,
        clearAllFilters: clearAllFilters,
        initActiveFilterStrip: initActiveFilterStrip,
        initFilterToolbar: initFilterToolbar,
    };
})();
